import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

type ContentType = 'review' | 'review_comment' | 'activity_comment' | 'message' | 'tag' | 'author_bio';
type ReportCategory = 'hate_speech' | 'harassment' | 'spam' | 'sexual_content' | 'violence' | 'misinformation' | 'other';

interface SubmitReportParams {
  contentType: ContentType;
  contentId: string;
  reportCategory: ReportCategory;
  additionalDetails?: string;
}

export function useContentReports() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const submitReport = async ({
    contentType,
    contentId,
    reportCategory,
    additionalDetails,
  }: SubmitReportParams) => {
    setIsSubmitting(true);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast({
          title: "Authentication required",
          description: "You must be signed in to report content.",
          variant: "destructive",
        });
        return false;
      }

      const { error } = await supabase
        .from('content_reports')
        .insert({
          reporter_id: user.id,
          content_type: contentType,
          content_id: contentId,
          report_category: reportCategory,
          additional_details: additionalDetails?.trim() || null,
        });

      if (error) {
        // Check for duplicate report
        if (error.code === '23505') {
          toast({
            title: "Already reported",
            description: "You've already reported this content. We're reviewing it.",
          });
          return false;
        }
        
        throw error;
      }

      toast({
        title: "Report submitted",
        description: "Thank you for reporting. We'll review this content shortly.",
      });
      
      return true;
    } catch (error: any) {
      console.error('Error submitting report:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to submit report. Please try again.",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    submitReport,
    isSubmitting,
  };
}
